import { createSignal, For, Show, onMount, createEffect, createMemo } from "solid-js";
import { useAdminContext } from "./AdminContext";
import { useUserContext } from "./UserContext";
import { useConnection } from "./ConnectionManager";
import socket from "./socket";
import { useI18n } from "./i18n";
import { printConvention } from "./printConvention";
import { ConventionDetails } from "./Conventions";

export default function AdminConventions() {
  const t = useI18n();
  const [adminStore, setAdminStore, adminActions] = useAdminContext();
  const [userStore] = useUserContext();
  const [connectionStore] = useConnection();
  
  const [promosWithConventions, setPromosWithConventions] = createSignal([]);
  const [selectedConvention, setSelectedConvention] = createSignal(null);
  const [loading, setLoading] = createSignal(true);
  const [lastUpdated, setLastUpdated] = createSignal(null);
  
  // Group conventions by status
  const groupedConventions = createMemo(() => {
    const data = promosWithConventions().length > 0 ? promosWithConventions() : adminStore.cachedConventions || [];
    const locked = [];
    const validated = [];
    const pending = [];
    
    data.forEach(promo => {
      const promoLocked = { ...promo, conventions: [] };
      const promoValidated = { ...promo, conventions: [] };
      const promoPending = { ...promo, conventions: [] };
      
      promo.conventions.forEach(conv => {
        if (conv.status === 'locked') {
          promoLocked.conventions.push(conv);
        } else if (conv.status === 'validated' || conv.info?.fullyValidated) {
          promoValidated.conventions.push(conv);
        } else {
          promoPending.conventions.push(conv);
        }
      });
      
      if (promoLocked.conventions.length > 0) locked.push(promoLocked);
      if (promoValidated.conventions.length > 0) validated.push(promoValidated);
      if (promoPending.conventions.length > 0) pending.push(promoPending);
    });
    
    return { locked, validated, pending };
  });
  
  // Immediately try to load cached data to avoid flicker when offline
  // This is especially important for new tabs opened in offline mode
  createEffect(() => {
    // Check if we have data in adminStore
    if (adminStore.cachedConventions !== null) {
      console.log("Using cached conventions data from AdminContext");
      setPromosWithConventions(adminStore.cachedConventions || []);
      
      // Get the timestamp from adminStore
      if (adminStore.conventionsLastUpdated) {
        console.log("Using conventions timestamp from AdminContext:", adminStore.conventionsLastUpdated);
        setLastUpdated(adminStore.conventionsLastUpdated);
      }
      
      setLoading(false);
    }
  });
  
  // Effect to react to connection status changes and data loading state
  createEffect(() => {
    // If we're offline, make sure we're displaying the right indicators
    if (connectionStore.isOffline) {
      // Only try to load if we don't already have conventions data (even if it's an empty array)
      if (adminStore.cachedConventions === null) {
        adminActions.loadAllConventions();
      } else {
        // We have data (might be empty), so stop showing loader
        setLoading(false);
      }
    }
    
    // Stop showing the loader when the admin store indicates conventions are no longer loading
    if (!adminStore.isLoadingConventions && loading()) {
      setLoading(false);
    }
  });
  
  onMount(() => {
    console.log("AdminConventions mounted, requesting data...");
    
    // Request conventions data if we're online and don't have data yet
    if (!connectionStore.isOffline && adminStore.cachedConventions === null) {
      adminActions.loadAllConventions();
    } else if (adminStore.cachedConventions !== null) {
      // We already have conventions data (even if empty array)
      setLoading(false);
    }
  });

  // Show convention details
  const showConventionDetails = (convention) => {
    setSelectedConvention(convention);
  };
  
  // Close convention details modal
  const closeModal = () => {
    setSelectedConvention(null);
  };
  
    // Print convention - uses external function
  const handlePrintConvention = (convention) => {
    printConvention(convention, adminStore.conventionStructure, promosWithConventions());
  };
  
  
  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return t("Not specified");
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  };
  
  // Get current year
  const currentYear = new Date().getFullYear();
  
  // Function to check if we have data but it's empty
  const hasEmptyData = createMemo(() => {
    return (
      !loading() && 
      (
        (Array.isArray(promosWithConventions()) && promosWithConventions().length === 0) ||
        (Array.isArray(adminStore.cachedConventions) && adminStore.cachedConventions.length === 0)
      )
    );
  });
  
  return (
    <div class="container mt-4">
      <h2 class="mb-4">{t("All Internship Agreements")} ({currentYear})</h2>
      
      {/* Offline indicator */}
      <Show when={connectionStore.isOffline}>
        <div class="alert alert-warning mb-4">
          <i class="fas fa-exclamation-triangle me-2"></i>
          <strong>{t("You are offline.")}</strong> {t("Viewing cached data in read-only mode.")}
          {lastUpdated() && (
            <div class="mt-1 small">
              {t("Last updated")}: {new Date(lastUpdated()).toLocaleString()}
            </div>
          )}
        </div>
      </Show>
      
      {/* Loading indicator */}
      <Show when={loading()}>
        <div class="text-center my-5">
          <div class="spinner-border" role="status"></div>
          <div class="mt-3">{t("Loading conventions...")}</div>
        </div>
      </Show>
      
      {/* Empty state */}
      <Show when={hasEmptyData()}>
        <div class="alert alert-info my-4">
          <i class="fas fa-info-circle me-2"></i>
          {t("No internship agreements found for the current year.")}
        </div>
      </Show>
      
      {/* Conventions data */}
      <Show when={!loading() && !hasEmptyData()}>
        {/* Locked Conventions Section */}
        <Show when={groupedConventions().locked.length > 0}>
          <h3 class="text-danger mb-3">
            <i class="fas fa-lock me-2"></i>
            {t("Locked Conventions")}
          </h3>
          <For each={groupedConventions().locked}>
            {(promo) => (
              <div class="mb-4">
                <h4 class="border-bottom pb-2">{promo.name}</h4>
                <div class="table-responsive">
                  <table class="table table-hover">
                    <thead>
                      <tr>
                        <th>{t("Student")}</th>
                        <th>{t("Business")}</th>
                        <th>{t("Period")}</th>
                        <th>{t("Actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={promo.conventions}>
                        {(convention) => (
                          <tr class="table-secondary">
                            <td>
                              {convention.fullname ? (
                                <span>{convention.fullname} <small class="text-muted">({convention.username})</small></span>
                              ) : (
                                convention.username
                              )}
                            </td>
                            <td>{convention.info?.business || t("Not specified")}</td>
                            <td>
                              {formatDate(convention.info?.startDate)} - {formatDate(convention.info?.endDate)}
                            </td>
                            <td>
                              <button
                                type="button"
                                class="btn btn-sm btn-outline-primary me-2"
                                onClick={() => showConventionDetails(convention)}
                              >
                                {t("View Details")}
                              </button>
                              <button
                                type="button"
                                class="btn btn-sm btn-info me-2"
                                onClick={() => handlePrintConvention(convention)}
                                title={t("Print this convention")}
                              >
                                <i class="fas fa-print me-1"></i>
                                {t("Print")}
                              </button>
                              <Show when={!connectionStore.isOffline}>
                                <button
                                  type="button"
                                  class="btn btn-sm btn-warning"
                                  onClick={() => adminActions.unlockConvention(convention.id)}
                                  title={t("Unlock this convention")}
                                >
                                  <i class="fas fa-unlock me-1"></i>
                                  {t("Unlock")}
                                </button>
                              </Show>
                            </td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </For>
        </Show>
        
        {/* Fully Validated Conventions Section */}
        <Show when={groupedConventions().validated.length > 0}>
          <h3 class="text-success mb-3">
            <i class="fas fa-check-circle me-2"></i>
            {t("Fully Validated Conventions")}
          </h3>
          <For each={groupedConventions().validated}>
            {(promo) => (
              <div class="mb-4">
                <h4 class="border-bottom pb-2">{promo.name}</h4>
                <div class="table-responsive">
                  <table class="table table-hover">
                    <thead>
                      <tr>
                        <th>{t("Student")}</th>
                        <th>{t("Business")}</th>
                        <th>{t("Period")}</th>
                        <th>{t("Actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={promo.conventions}>
                        {(convention) => (
                          <tr class="table-success">
                            <td>
                              {convention.fullname ? (
                                <span>{convention.fullname} <small class="text-muted">({convention.username})</small></span>
                              ) : (
                                convention.username
                              )}
                            </td>
                            <td>{convention.info?.business || t("Not specified")}</td>
                            <td>
                              {formatDate(convention.info?.startDate)} - {formatDate(convention.info?.endDate)}
                            </td>
                            <td>
                              <button
                                type="button"
                                class="btn btn-sm btn-outline-primary me-2"
                                onClick={() => showConventionDetails(convention)}
                              >
                                {t("View Details")}
                              </button>
                              <Show when={!connectionStore.isOffline}>
                                <button
                                  type="button"
                                  class="btn btn-sm btn-danger"
                                  onClick={() => adminActions.lockConvention(convention.id)}
                                  title={t("Lock this convention")}
                                >
                                  <i class="fas fa-lock me-1"></i>
                                  {t("Lock this convention")}
                                </button>
                              </Show>
                            </td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </For>
        </Show>
        
        {/* Pending Validation Conventions Section */}
        <Show when={groupedConventions().pending.length > 0}>
          <h3 class="text-warning mb-3">
            <i class="fas fa-clock me-2"></i>
            {t("Pending Teacher Validation")}
          </h3>
          <For each={groupedConventions().pending}>
            {(promo) => (
              <div class="mb-4">
                <h4 class="border-bottom pb-2">{promo.name}</h4>
                <div class="table-responsive">
                  <table class="table table-hover">
                    <thead>
                      <tr>
                        <th>{t("Student")}</th>
                        <th>{t("Business")}</th>
                        <th>{t("Period")}</th>
                        <th>{t("Validation")}</th>
                        <th>{t("Actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={promo.conventions}>
                        {(convention) => (
                          <tr>
                            <td>
                              {convention.fullname ? (
                                <span>{convention.fullname} <small class="text-muted">({convention.username})</small></span>
                              ) : (
                                convention.username
                              )}
                            </td>
                            <td>{convention.info?.business || t("Not specified")}</td>
                            <td>
                              {formatDate(convention.info?.startDate)} - {formatDate(convention.info?.endDate)}
                            </td>
                            <td>
                              <div class="d-flex">
                                <Show when={promo.validators && promo.validators.length > 0}>
                                  <For each={promo.validators}>
                                    {(validator) => {
                                      const hasValidated = convention.info?.validations?.some(
                                        v => v.validatorId === validator.id
                                      );
                                      return (
                                        <div
                                          class={`rounded-circle me-1 ${hasValidated ? 'bg-success' : 'bg-danger'}`}
                                          style="width: 24px; height: 24px;"
                                          title={hasValidated
                                            ? `${t("Validated by")} ${validator.fullname ? `${validator.fullname} (${validator.username})` : validator.username}`
                                            : `${t("Not validated by")} ${validator.fullname ? `${validator.fullname} (${validator.username})` : validator.username}`}
                                        ></div>
                                      );
                                    }}
                                  </For>
                                </Show>
                                <Show when={!promo.validators || promo.validators.length === 0}>
                                  <span class="badge bg-warning text-dark">{t("No validators")}</span>
                                </Show>
                              </div>
                            </td>
                            <td>
                              <button
                                type="button"
                                class="btn btn-sm btn-outline-primary"
                                onClick={() => showConventionDetails(convention)}
                              >
                                {t("View Details")}
                              </button>
                            </td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </For>
        </Show>
      </Show>
      
      {/* Modal for convention details */}
      <Show when={selectedConvention()}>
        <div class="modal-overlay" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(0,0,0,0.5); z-index: 2000; display: flex; align-items: center; justify-content: center;">
          <div class="modal-content bg-white p-4 rounded shadow-lg" style="width: 90%; max-width: 800px; max-height: 90vh; overflow-y: auto;">
            <div class="d-flex justify-content-between align-items-start mb-3">
              <h3>{selectedConvention().info?.title || `${t("Internship Agreement")} (${selectedConvention().year})`}</h3>
              <button
                type="button"
                class="btn-close"
                aria-label="Close"
                onClick={closeModal}
              ></button>
            </div>
            <div class="mb-4">
              <Show when={connectionStore.isOffline}>
                <div class="alert alert-warning mb-3">
                  <i class="fas fa-exclamation-triangle me-2"></i>
                  <strong>{t("You are offline.")}</strong> {t("Viewing cached data in read-only mode.")}
                </div>
              </Show>
            </div>
            <ConventionDetails 
              convention={selectedConvention()} 
              startEditing={null} 
              conventionStructure={adminStore.conventionStructure} 
              />
            <div class="mb-4">
              <h5>{t("Validation Status")}</h5>
              <Show when={selectedConvention().info?.validations && selectedConvention().info?.validations.length > 0}>
                <ul class="list-group">
                  <For each={selectedConvention().info.validations}>
                    {(validation) => (
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        <span>{validation.validatorName}</span>
                        <span class="text-muted">{new Date(validation.timestamp).toLocaleString()}</span>
                      </li>
                    )}
                  </For>
                </ul>
              </Show>
              <Show when={!selectedConvention().info?.validations || selectedConvention().info?.validations.length === 0}>
                <p>{t("Not validated by any teacher yet.")}</p>
              </Show>
            </div>
            <div class="mb-4">
              <Show when={!selectedConvention().info?.feedbackHistory
                || selectedConvention().info.feedbackHistory.length == 0
                || selectedConvention().info.feedbackHistory[0].conventionVersion != selectedConvention().info.version}>
                <p>{t("No feedback for current version")}</p>
              </Show>
              {/* Feedback History */}
              <Show when={selectedConvention().info?.feedbackHistory && selectedConvention().info.feedbackHistory.length > 0}>
                <div class="mt-3">
                  <h5>{t("Feedback History")}</h5>
                  <div class="list-group">
                    <For each={selectedConvention().info.feedbackHistory}>
                      {(historyItem) => (
                        <div class="list-group-item">
                          <div class="d-flex justify-content-between align-items-center mb-1">
                            <strong>{t("Version")} {historyItem.conventionVersion || '?'}</strong>
                            <small class="text-muted">
                              {t("by")} {historyItem.teacher} {t("on")} {new Date(historyItem.date).toLocaleString()}
                            </small>
                          </div>
                          <p class="mb-1">{historyItem.feedback}</p>
                          <div class="d-flex gap-2">
                            <Show when={historyItem.notificationSent}>
                              <small class="badge bg-info">
                                <i class="fas fa-envelope me-1"></i> {t("Notification sent")}
                              </small>
                            </Show>
                            <Show when={historyItem.validations && historyItem.validations.length > 0}>
                              <small class="text-muted">
                                {t("Validated by")} {historyItem.validations.length} {t("teacher(s)")}
                              </small>
                            </Show>
                          </div>
                        </div>
                      )}
                    </For>
                  </div>
                </div>
              </Show>
            </div>
            <div class="text-end">
              <button
                type="button"
                class="btn btn-secondary"
                onClick={closeModal}
              >
                {t("Close")}
              </button>
            </div>
          </div>
        </div>
      </Show>
    </div>
  );
}